// ===================================================================
// src/app/lib/api.ts
//
// Cliente HTTP com suporte a JWT.
//
// - Injeta Authorization: Bearer {access} em toda request
// - Intercepta 401 → tenta refresh automático
// - Se refresh falhar → limpa tokens e redireciona pro login
// ===================================================================

import { ENV } from './env';
import { loginPathFor } from './loginRedirect';

const TOKEN_KEYS = {
  access: 'access_token',
  refresh: 'refresh_token',
} as const;

// ── Token helpers ────────────────────────────────────────────────

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.access);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.refresh);
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(TOKEN_KEYS.access, access);
  localStorage.setItem(TOKEN_KEYS.refresh, refresh);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEYS.access);
  localStorage.removeItem(TOKEN_KEYS.refresh);
}

// ── Refresh lock (evita refresh duplicado) ───────────────────────
//
// Singleton compartilhado com src/app/services/api.ts — os dois clientes
// HTTP do projeto leem/escrevem as mesmas chaves de localStorage
// (access_token/refresh_token), então precisam de um único refresh
// em andamento por vez para não invalidar o refresh token um do outro
// quando o backend faz rotação (ROTATE_REFRESH_TOKENS).

let refreshPromise: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  // Se já tem um refresh em andamento, espera ele
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refresh = getRefreshToken();
    if (!refresh) return null;

    try {
      const res = await fetch(`${ENV.authUrl}/token/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      });

      if (!res.ok) {
        clearTokens();
        return null;
      }

      const data = await res.json();

      // O backend com ROTATE_REFRESH_TOKENS retorna novo refresh
      setTokens(data.access, data.refresh ?? refresh);
      return data.access as string;
    } catch {
      clearTokens();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ── Fetch wrapper ────────────────────────────────────────────────

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Se true, não injeta Authorization header */
  noAuth?: boolean;
  /**
   * Se true, não faz window.location.href='/login' quando o refresh falha.
   * Usar em verificações silenciosas de sessão (checkSession, init da app)
   * para evitar hard redirect em rotas públicas (/invite, /login, /signup)
   * antes do React sequer avaliar isAuthenticated.
   */
  noRedirect?: boolean;
}

/**
 * Wrapper do fetch com JWT automático.
 *
 * Uso:
 *   const data = await api<User[]>('/api/v1/auth/users/');
 *   const res  = await api('/api/v1/auth/login/', { method: 'POST', body: {...}, noAuth: true });
 */
export async function api<T = unknown>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const { body, noAuth, noRedirect, headers: extraHeaders, ...rest } = options;

  const url = path.startsWith('http') ? path : `${ENV.API_URL}${path}`;

  // FormData (upload de arquivo, ex: avatar) não pode ir com Content-Type
  // fixo — o browser precisa definir o boundary do multipart sozinho.
  const isFormData = body instanceof FormData;

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(extraHeaders as Record<string, string>),
  };

  if (!noAuth) {
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const resolveBody = () => (isFormData ? (body as FormData) : body ? JSON.stringify(body) : undefined);

  let res = await fetch(url, {
    ...rest,
    headers,
    body: resolveBody(),
  });

  // ── Auto-refresh em 401 ──────────────────────────────────
  if (res.status === 401 && !noAuth) {
    const newToken = await refreshAccessToken();

    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(url, {
        ...rest,
        headers,
        body: resolveBody(),
      });
    } else {
      // Refresh falhou → redireciona pro login
      clearTokens();
      if (!noRedirect) {
        window.location.href = loginPathFor(window.location.pathname + window.location.search);
      }
      throw new Error('Session expired');
    }
  }

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new ApiError(res.status, errorData);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

// ── Error class ──────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public status: number,
    public data: Record<string, unknown>,
  ) {
    const msg =
      typeof data === 'object'
        ? Object.values(data).flat().join('. ')
        : String(data);
    super(msg || `API Error ${status}`);
    this.name = 'ApiError';
  }
}